import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DataTable,
  DataTableProps,
  DataTableValue,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnProps } from "primereact/column";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import TextFieldWithPrefix from "../TextFieldWithPrefix/TextFieldWithPrefix";
import "./CustomTable.scss";
import { ChangeField } from "../../types/types";
import { useNavigate, useLocation } from "react-router-dom";

// Extend jsPDF type to include autoTable

export interface TableColumn<T> extends Omit<ColumnProps, "field"> {
  field: keyof T;
  customBody?: (rowData: T) => React.ReactNode;
  width?: string;
  maxWidth?: string;
  filterable?: boolean;
}

type DropdownOption = {
  label: string | number | symbol;
  value: string | number | symbol;
};

interface CustomDataTableProps<T extends DataTableValue>
  extends Omit<DataTableProps<T[]>, "value" | "children"> {
  exportCSV?: any;
  data: Readonly<T[]>;
  columns: TableColumn<T>[];
  globalFilterEnabled?: boolean;
  rowsPerPage?: number;
  tableTitle?: string;
  rowCount?: number;
  HeaderComponent?: React.ReactNode | (() => React.ReactNode);
  onPageChange?: (event: any) => void;
  footerGroup?: any;
  exportCSVShow?: boolean;
  exportExcelShow?: boolean;
  onSearchClear?: () => void;
  first?: number;
  clearFilter?: (state: any) => void;
}

export function CustomDataTable<T extends object>({
  data,
  columns,
  globalFilterEnabled = true,
  rowsPerPage,
  tableTitle,
  HeaderComponent,
  rowCount,
  footerGroup,
  exportCSVShow,
  exportExcelShow,
  onSearchClear,
  first,
  clearFilter,
  ...rest
}: CustomDataTableProps<T>) {
  // Remove selectionMode from rest if present to avoid type errors
  const { onPageChange, selectionMode, ...filteredRest } = rest as any;
  const textFieldRef = useRef<{ clear: () => void }>(null);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [searchField, setSearchField] = React.useState({
    prefix: "",
    text: "",
  });
  const [selectedProducts, setSelectedProducts] = useState<
    CustomDataTableProps<T>[] | null
  >(null);
  const navigate = useNavigate();
  const location = useLocation();
  const DropdownMenu = columns.reduce<DropdownOption[]>((acc, data) => {
    if (data.filterable) {
      acc.push({ label: data.field, value: data.field });
    }
    return acc;
  }, []);
  const dt = useRef<CustomDataTableProps<T>>(null);
  const params = new URLSearchParams(window.location.search);

  const sortIconTemplate = (options: { sortOrder: 1 | -1 | 0 | undefined }) => {
    if (options.sortOrder === 1) {
      return <ArrowUpwardIcon fontSize="small" />;
    } else if (options.sortOrder === -1) {
      return <ArrowDownwardIcon fontSize="small" />;
    } else {
      return <ImportExportIcon fontSize="small" />; // default unsorted icon
    }
  };
  const HandleSearchData = (data: ChangeField) => {
    setSearchField(data);
  };
  const exportColumns = columns.map((col) => ({
    title: col.header,
    dataKey: col.field,
  }));
  const exportCSV = (selectionOnly: any) => {
    if (dt.current) {
      dt.current.exportCSV({ selectionOnly });
    }
  };
  const generateFileName = (text:any) => {
    return text.replace(/\s+/g, "").toLowerCase();
  };
  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet([...data]);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, generateFileName(tableTitle || "data"));
    });
  };
  const formatDateTime = () => {
    const d = new Date();

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`;
  };
  const saveAsExcelFile = (buffer: BlobPart, fileName: string) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          data,
          fileName + "_" + formatDateTime() + EXCEL_EXTENSION
        );
      }
    });
  };

  const handleOnSearch = () => {
    const url = new URLSearchParams(location.search);

    if (searchField?.prefix && searchField?.text) {
      url.set("prefix", searchField.prefix);
      url.set("text", searchField.text);

      navigate(`${location.pathname}?${url.toString()}`, { replace: true });
    }
  };

  const handleOnClear = () => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete("prefix");
    urlParams.delete("text");
    textFieldRef.current?.clear();
    setSearchField({ prefix: "", text: "" });
    onSearchClear && onSearchClear();
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
    clearFilter && clearFilter(true);
  };

  const header = (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      flexWrap="wrap"
      mb={2}
    >
      {tableTitle && (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            {tableTitle}
          </Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center" gap={1} className="tableHeaderBox">
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap={"wrap"}
          justifyContent={"center"}
          className="filterBoxPanel"
        >
          <TextFieldWithPrefix
            ref={textFieldRef}
            prefixOptions={DropdownMenu}
            onChange={HandleSearchData}
            defaultPrefix={searchField.prefix || undefined}
            defaultValue={searchField.text || ""}
          />
          <Button
            variant="contained"
            color="primary"
            className="filterBtn"
            onClick={handleOnSearch}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="filterBtn"
            onClick={handleOnClear}
          >
            Clear
          </Button>
          {typeof HeaderComponent === "function"
            ? HeaderComponent()
            : HeaderComponent}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          alignSelf={"flex-start"}
          className="exportBtnBox"
        >
          {exportCSVShow && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => exportCSV(false)}
              data-pr-tooltip="CSV"
              startIcon={<ImportExportIcon />}
            >
              CSV
            </Button>
          )}
          {exportExcelShow && (
            <Button
              variant="contained"
              color="success"
              onClick={exportExcel}
              data-pr-tooltip="XLS"
              startIcon={<ImportExportIcon />}
            >
              Excel
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <DataTable
      ref={dt}
      value={[...data]}
      paginator
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReportTemplate RowsPerPageDropdown"
      rowsPerPageOptions={[10, 25, 50]}
      currentPageReportTemplate="Show result:"
      rows={rowsPerPage}
      globalFilter={globalFilterEnabled ? globalFilter : undefined}
      header={globalFilterEnabled ? header : null}
      responsiveLayout="scroll"
      scrollable
      totalRecords={rowCount}
      first={first ?? 0} 
      scrollHeight="flex"
      className="p-datatable-sm"
      // selectionMode={'checkbox'}
      // selection={selectedProducts!}
      sortIcon={sortIconTemplate}
      onSelectionChange={(e: any) => setSelectedProducts(e.value)}
      onPage={rest.onPageChange}
      footerColumnGroup={footerGroup}
      {...filteredRest}
    >
      {/* <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column> */}
      {columns.map((col, idx) => {
        const { field, customBody, width, maxWidth, ...colProps } = col;
        return (
          <Column
            style={{
              minWidth: width,
              width: width || "auto",
              maxWidth: maxWidth || "auto",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            key={String(field) + idx}
            field={typeof field === "string" ? field : String(field)}
            header={col.header}
            // filter
            // sortable
            body={customBody}
            {...colProps}
          />
        );
      })}
    </DataTable>
  );
}
